import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    AlertTriangle,
    Camera,
    Car,
    CheckCircle2,
    Download,
    Eye,
    FileText,
    Globe,
    Loader2,
    RefreshCw,
    ShoppingCart,
    Sparkles,
    Upload,
    Zap,
} from "lucide-react";
import { toast } from "sonner";
import { ekspertizHasar, hasarSatinAlma } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AracKontekstBar from "./ekspertiz-hasar/AracKontekstBar";

const ADIMLAR = [
    { id: "ruhsat", label: "Ruhsat", icon: FileText },
    { id: "foto", label: "Kaza Foto", icon: Camera },
    { id: "arac", label: "Araç & Rayiç", icon: Car },
    { id: "teklif", label: "Teklif", icon: ShoppingCart },
];

function tl(n, fmt) {
    if (fmt) return fmt;
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    if (Math.abs(v) < 0.005) return "—";
    const whole = Math.abs(v - Math.round(v)) < 0.01;
    if (whole) return `₺${Math.round(v).toLocaleString("tr-TR")}`;
    const [main, dec] = v.toFixed(2).split(".");
    return `₺${Number(main).toLocaleString("tr-TR")},${dec}`;
}

function apiHata(e, varsayilan = "İşlem başarısız") {
    const ham = e?.response?.data?.error || e?.response?.data?.detail || e?.message || "";
    if (/timeout|timed out|504/i.test(String(ham))) {
        return "İşlem zaman aşımına uğradı. Daha az fotoğraf veya hızlı mod deneyin.";
    }
    return ham || varsayilan;
}

function Kpi({ label, value, hint, accent = "gold" }) {
    const ring =
        accent === "red"
            ? "border-rose-500/30 bg-rose-500/5"
            : accent === "emerald"
              ? "border-emerald-500/30 bg-emerald-500/5"
              : accent === "sky"
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl text-white mt-1">{value ?? "—"}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function AdimGosterge({ aktif, ruhsatOk, fotoOk, aracOk, teklifOk }) {
    const durum = { ruhsat: ruhsatOk, foto: fotoOk, arac: aracOk, teklif: teklifOk };
    return (
        <div className="flex flex-wrap gap-2">
            {ADIMLAR.map((a, i) => {
                const Icon = a.icon;
                const ok = durum[a.id];
                const aktifAdim = a.id === aktif;
                return (
                    <div
                        key={a.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
                            aktifAdim
                                ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3E5AB]"
                                : ok
                                  ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
                                  : "border-white/10 bg-white/[0.02] text-slate-500"
                        }`}
                    >
                        {ok ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                        ) : (
                            <Icon className="h-3.5 w-3.5" />
                        )}
                        <span>{i + 1}. {a.label}</span>
                    </div>
                );
            })}
        </div>
    );
}

export default function HasarSatinAlmaPage() {
    const [searchParams] = useSearchParams();
    const deepDosyaId = searchParams.get("dosya");

    const [ozet, setOzet] = useState(null);
    const [dosyalar, setDosyalar] = useState([]);
    const [seciliDosya, setSeciliDosya] = useState(null);
    const [loading, setLoading] = useState(false);
    const [adim, setAdim] = useState("ruhsat");

    const [ruhsatSonuc, setRuhsatSonuc] = useState(null);
    const [fotoSonuc, setFotoSonuc] = useState(null);
    const [fotoOnizleme, setFotoOnizleme] = useState([]);

    const [marka, setMarka] = useState("");
    const [model, setModel] = useState("");
    const [modelYili, setModelYili] = useState("");
    const [plaka, setPlaka] = useState("");
    const [km, setKm] = useState("");
    const [rayicDeger, setRayicDeger] = useState("");
    const [muvekkilAd, setMuvekkilAd] = useState("");
    const [derinArastir, setDerinArastir] = useState(true);

    const [teklif, setTeklif] = useState(null);
    const [onizleHtml, setOnizleHtml] = useState(null);
    const [onizleAcik, setOnizleAcik] = useState(false);

    const aracPayload = useMemo(
        () => ({
            marka: marka || undefined,
            model: model || undefined,
            model_yili: modelYili ? Number(modelYili) : undefined,
            plaka: plaka || undefined,
            km: km ? Number(km) : undefined,
            rayic_deger: rayicDeger ? Number(rayicDeger) : undefined,
        }),
        [marka, model, modelYili, plaka, km, rayicDeger],
    );

    const aracSenkron = useCallback((kaynak) => {
        if (!kaynak) return;
        if (kaynak.marka) setMarka(kaynak.marka);
        if (kaynak.model) setModel(kaynak.model);
        if (kaynak.model_yili) setModelYili(String(kaynak.model_yili));
        if (kaynak.plaka) setPlaka(kaynak.plaka);
        if (kaynak.km != null) setKm(String(kaynak.km));
    }, []);

    const refresh = useCallback(async () => {
        try {
            const [o, d] = await Promise.all([hasarSatinAlma.ozet(), ekspertizHasar.dosyalar()]);
            setOzet(o);
            setDosyalar(d.dosyalar || []);
        } catch {
            toast.error("Özet yüklenemedi");
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!deepDosyaId || !dosyalar.length) return;
        const hit = dosyalar.find((x) => String(x.id) === String(deepDosyaId));
        if (hit) {
            setSeciliDosya(hit);
            aracSenkron(hit);
            if (hit.arac_rayic_degeri) setRayicDeger(String(hit.arac_rayic_degeri));
            if (hit.muvekkil_ad) setMuvekkilAd(hit.muvekkil_ad);
        }
    }, [deepDosyaId, dosyalar, aracSenkron]);

    useEffect(() => {
        if (ruhsatSonuc?.arac) aracSenkron(ruhsatSonuc.arac);
    }, [ruhsatSonuc, aracSenkron]);

    useEffect(() => {
        if (fotoSonuc?.arac_tahmin) aracSenkron(fotoSonuc.arac_tahmin);
    }, [fotoSonuc, aracSenkron]);

    const ruhsatYukle = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLoading(true);
        setAdim("ruhsat");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await hasarSatinAlma.ruhsatAnaliz(fd);
            if (!r.success) throw new Error(r.error || "Ruhsat analizi başarısız");
            setRuhsatSonuc(r);
            if (r.arac?.muvekkil_ad) setMuvekkilAd(r.arac.muvekkil_ad);
            toast.success(r.tespit_ozeti || "Ruhsat okundu — araç bilgileri dolduruldu");
            setAdim("foto");
        } catch (err) {
            toast.error(apiHata(err, "Ruhsat analizi başarısız"));
        } finally {
            setLoading(false);
            e.target.value = "";
        }
    };

    const fotoYukle = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setLoading(true);
        setAdim("foto");
        setFotoOnizleme(files.map((f) => URL.createObjectURL(f)));
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const r = await hasarSatinAlma.fotoAnaliz(fd);
            if (!r.success) throw new Error(r.error || "Foto analizi başarısız");
            setFotoSonuc(r);
            toast.success(r.ozet || `${r.parcalar?.length || 0} parça tespit edildi`);
            if (r.uyarilar?.length) {
                toast.warning(r.uyarilar[0]);
            }
            setAdim("arac");
        } catch (err) {
            toast.error(apiHata(err, "Kaza foto analizi başarısız"));
        } finally {
            setLoading(false);
            e.target.value = "";
        }
    };

    const tamAnalizCalistir = async () => {
        if (!marka) {
            toast.error("Marka bilgisi gerekli");
            setAdim("arac");
            return;
        }
        const parcaSayisi =
            (fotoSonuc?.parcalar?.length || 0) + (ruhsatSonuc ? 0 : 0);
        if (parcaSayisi === 0 && !fotoSonuc?.parcalar?.length) {
            toast.error("Önce kaza fotoğrafı yükleyin veya parça listesi oluşturun");
            setAdim("foto");
            return;
        }

        setLoading(true);
        setAdim("teklif");
        try {
            const r = await hasarSatinAlma.tamAnaliz({
                arac: aracPayload,
                ruhsat_sonuc: ruhsatSonuc,
                foto_sonuc: fotoSonuc,
                foto_yollari: fotoSonuc?.foto_yollari,
                rayic_deger: rayicDeger ? Number(rayicDeger) : undefined,
                km: km ? Number(km) : undefined,
                dosya_id: seciliDosya?.id,
                derin: derinArastir,
                muvekkil_ad: muvekkilAd || seciliDosya?.muvekkil_ad,
            });
            if (!r.success) throw new Error(r.error || "Teklif hesaplanamadı");
            if (!r.teklif?.oneri) {
                toast.warning("Hasar bedeli hesaplanamadı — parça/işçilik/boya dökümünü kontrol edin");
            }
            setTeklif(r);
            toast.success("MBS Hasar Satın Alma teklifi hazır");
        } catch (err) {
            toast.error(apiHata(err, "Teklif hesaplanamadı"));
        } finally {
            setLoading(false);
        }
    };

    const teklifOnizle = async () => {
        if (!teklif) return;
        setLoading(true);
        try {
            const r = await hasarSatinAlma.teklifOnizle({ teklif });
            setOnizleHtml(r.html);
            setOnizleAcik(true);
        } catch (err) {
            toast.error(apiHata(err, "Önizleme oluşturulamadı"));
        } finally {
            setLoading(false);
        }
    };

    const teklifPdfIndir = async () => {
        if (!teklif) return;
        setLoading(true);
        try {
            const blob = await hasarSatinAlma.teklifPdf({ teklif });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `mbs-hasar-satin-alma-${teklif.teklif_no || "teklif"}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (err) {
            toast.error(err.message || "PDF indirilemedi");
        } finally {
            setLoading(false);
        }
    };

    const oz = teklif?.ozet || {};
    const teklifDetay = teklif?.teklif || {};

    return (
        <div className="p-4 sm:p-6 space-y-6">
            {/* Hero */}
            <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#121A2F] via-[#0f172a] to-[#121A2F] p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest font-semibold">
                            <Sparkles className="h-4 w-4" />
                            MBS Hukuk — Hasar Satın Alma
                        </div>
                        <h2 className="font-display text-xl sm:text-2xl text-white mt-2">
                            Ruhsat + Kaza Foto → Piyasa Fiyat → Teklif
                        </h2>
                        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
                            Ruhsattan araç kimliği, kaza fotoğraflarından AI parça tespiti, OEM/muadil
                            internet piyasa taraması ve standart işçilik/boya maliyetleri ile{" "}
                            <strong className="text-slate-200">hasar satın alma teklifi</strong> üretilir.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-white/10"
                            onClick={refresh}
                            disabled={loading}
                        >
                            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                        </Button>
                        <Link
                            to="/yeni/rayic-parca-merkezi/hasar/uzman"
                            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#D4AF37] px-3 py-2 rounded-lg border border-white/10"
                        >
                            Hasar Uzmanı →
                        </Link>
                    </div>
                </div>

                <div className="mt-5">
                    <AdimGosterge
                        aktif={adim}
                        ruhsatOk={!!ruhsatSonuc}
                        fotoOk={!!fotoSonuc?.parcalar?.length}
                        aracOk={!!marka && !!rayicDeger}
                        teklifOk={!!teklif?.teklif?.oneri}
                    />
                </div>
            </div>

            {/* Workflow cards */}
            <div className="grid lg:grid-cols-2 gap-4">
                {/* Ruhsat */}
                <div className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <FileText className="h-5 w-5 text-[#D4AF37]" />
                        <h3 className="text-sm font-medium text-white">1. Ruhsat OCR / AI</h3>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">
                        Ruhsat fotoğrafı veya PDF — marka, model, plaka, şasi otomatik çıkarılır.
                    </p>
                    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/10 bg-black/20 px-4 py-8 cursor-pointer hover:border-[#D4AF37]/40 transition">
                        <Upload className="h-8 w-8 text-slate-500" />
                        <span className="text-sm text-slate-300">Ruhsat yükle</span>
                        <span className="text-[10px] text-slate-500">PDF, JPG, PNG</span>
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={ruhsatYukle} />
                    </label>
                    {ruhsatSonuc?.arac ? (
                        <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-200">
                            <CheckCircle2 className="h-4 w-4 inline mr-1" />
                            {ruhsatSonuc.arac.marka} {ruhsatSonuc.arac.model}{" "}
                            {ruhsatSonuc.arac.model_yili} — {ruhsatSonuc.arac.plaka || "plaka yok"}
                        </div>
                    ) : null}
                </div>

                {/* Kaza foto */}
                <div className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Camera className="h-5 w-5 text-[#D4AF37]" />
                        <h3 className="text-sm font-medium text-white">2. Kaza Fotoğraf AI</h3>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">
                        Hasarlı bölgeler ve parçalar vision AI ile tespit edilir (en fazla 8 foto).
                    </p>
                    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/10 bg-black/20 px-4 py-8 cursor-pointer hover:border-[#D4AF37]/40 transition">
                        <Camera className="h-8 w-8 text-slate-500" />
                        <span className="text-sm text-slate-300">Kaza fotoğrafları yükle</span>
                        <span className="text-[10px] text-slate-500">Çoklu seçim desteklenir</span>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={fotoYukle}
                        />
                    </label>
                    {fotoOnizleme.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {fotoOnizleme.slice(0, 4).map((src, i) => (
                                <img
                                    key={src}
                                    src={src}
                                    alt=""
                                    className="h-14 w-14 rounded-lg object-cover border border-white/10"
                                />
                            ))}
                        </div>
                    ) : null}
                    {fotoSonuc ? (
                        <div className="mt-4 space-y-2">
                            <p className="text-xs text-slate-400">{fotoSonuc.ozet}</p>
                            <div className="flex flex-wrap gap-1">
                                {(fotoSonuc.parcalar || []).slice(0, 6).map((p, i) => (
                                    <span
                                        key={i}
                                        className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300"
                                    >
                                        {p.parca_adi} ({p.islem_tipi})
                                    </span>
                                ))}
                                {(fotoSonuc.parcalar?.length || 0) > 6 ? (
                                    <span className="text-[10px] text-slate-500">
                                        +{fotoSonuc.parcalar.length - 6}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Araç + dosya */}
            <div className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Car className="h-5 w-5 text-[#D4AF37]" />
                    <h3 className="text-sm font-medium text-white">3. Araç, Rayiç & Dosya</h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">Sigorta dosyası</label>
                        <select
                            className="mt-1 w-full h-9 rounded-md bg-[#0f172a] border border-white/10 text-sm text-white px-3"
                            value={seciliDosya?.id || ""}
                            onChange={(e) => {
                                const hit = dosyalar.find((d) => String(d.id) === e.target.value);
                                setSeciliDosya(hit || null);
                                if (hit) {
                                    aracSenkron(hit);
                                    if (hit.arac_rayic_degeri) setRayicDeger(String(hit.arac_rayic_degeri));
                                    if (hit.muvekkil_ad) setMuvekkilAd(hit.muvekkil_ad);
                                }
                            }}
                        >
                            <option value="">— Dosya seç (opsiyonel) —</option>
                            {dosyalar.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.dosya_no || d.id} — {d.plaka || d.muvekkil_ad || "Dosya"}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">Müvekkil adı</label>
                        <Input
                            value={muvekkilAd}
                            onChange={(e) => setMuvekkilAd(e.target.value)}
                            placeholder="Teklif PDF için"
                            className="mt-1 bg-[#0f172a] border-white/10 h-9 text-sm"
                        />
                    </div>
                </div>

                <AracKontekstBar
                    arac={{ tam_etiket: marka && model ? `${marka} ${model} ${modelYili}` : null }}
                    marka={marka}
                    model={model}
                    modelYili={modelYili}
                    onMarka={setMarka}
                    onModel={setModel}
                    onModelYili={setModelYili}
                    seciliDosya={seciliDosya}
                />

                <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">Plaka</label>
                        <Input
                            value={plaka}
                            onChange={(e) => setPlaka(e.target.value)}
                            className="mt-1 bg-[#0f172a] border-white/10 h-9 text-sm"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">KM</label>
                        <Input
                            type="number"
                            value={km}
                            onChange={(e) => setKm(e.target.value)}
                            className="mt-1 bg-[#0f172a] border-white/10 h-9 text-sm"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">Piyasa rayiç (TL)</label>
                        <Input
                            type="number"
                            value={rayicDeger}
                            onChange={(e) => setRayicDeger(e.target.value)}
                            placeholder="Kaza öncesi değer"
                            className="mt-1 bg-[#0f172a] border-white/10 h-9 text-sm"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                    <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={derinArastir}
                            onChange={(e) => setDerinArastir(e.target.checked)}
                            className="rounded border-white/20"
                        />
                        <Globe className="h-3.5 w-3.5" />
                        Derin piyasa taraması (Google/N11/Gemini — önerilir)
                    </label>
                </div>

                <Button
                    onClick={tamAnalizCalistir}
                    disabled={loading}
                    className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#c4a030] text-slate-900 font-bold"
                >
                    {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <Zap className="h-4 w-4 mr-2" />
                    )}
                    Tam analiz & teklif hesapla
                </Button>
            </div>

            {/* Metodoloji notları */}
            {ozet?.metodoloji_notlari ? (
                <div className="rounded-xl border border-white/5 bg-black/20 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Metodoloji</p>
                    <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
                        {ozet.metodoloji_notlari.map((n, i) => (
                            <li key={i}>{n}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {/* Teklif sonuç */}
            {teklif ? (
                <div className="space-y-4">
                    {oz.ekonomik_pert ? (
                        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-rose-200">Ekonomik pert uyarısı</p>
                                <p className="text-xs text-rose-200/80 mt-1">
                                    Onarım maliyeti rayiç değerin %70&apos;ini aşıyor — hasar satın alma
                                    teklifi bu bağlamda değerlendirilmelidir.
                                </p>
                            </div>
                        </div>
                    ) : null}

                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        <Kpi label="Hasar satın alma bedeli" value={tl(teklifDetay.oneri)} accent="gold" hint="Görünen hasar bazlı" />
                        <Kpi
                            label="Pazarlık aralığı"
                            value={`${tl(teklifDetay.min)} – ${tl(teklifDetay.max)}`}
                            hint="Min / max band"
                        />
                        <Kpi label="Rayiç (referans)" value={tl(oz.rayic_deger)} hint="Teklife dahil değil" />
                        <Kpi
                            label="Onarım maliyeti (görünen)"
                            value={tl(oz.toplam_onarim_maliyeti)}
                            accent={oz.ekonomik_pert ? "red" : "sky"}
                            hint="Muhtemel gizli hariç"
                        />
                        <Kpi
                            label="Güven skoru"
                            value={`%${teklif.guven_skoru ?? "—"}`}
                            hint={`${oz.gorunen_parca ?? "—"} görünen · ${oz.gizli_parca ?? 0} gizli`}
                            accent="emerald"
                        />
                    </div>

                    {teklif.kurumsal?.kalemler ? (
                        <div className="rounded-xl border border-[#D4AF37]/20 bg-[#121A2F]/80 p-5 space-y-3">
                            <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold">
                                Kurumsal değerlendirme özeti
                            </p>
                            <div className="grid md:grid-cols-2 gap-3">
                                {teklif.kurumsal.kalemler.map((k, i) => (
                                    <div key={i} className="rounded-lg border border-white/10 bg-black/20 p-3">
                                        <p className="text-xs font-medium text-white mb-2">{k.baslik}</p>
                                        <ul className="text-[11px] text-slate-400 space-y-1 list-disc list-inside">
                                            {(k.maddeler || []).map((m, j) => (
                                                <li key={j}>{m}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {/* Teklif hero box */}
                    <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1a0a0a] via-[#121A2F] to-[#0f172a] p-6 text-center">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                            Hasar Satın Alma Bedeli
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{teklif.teklif_no}</p>
                        <div className="font-display text-4xl sm:text-5xl text-white mt-4">
                            {tl(teklifDetay.oneri)}
                        </div>
                        <p className="text-sm text-amber-200/90 mt-2 font-medium">
                            Araç satın alma değil — hasar/onarım bedeli teklifidir
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                            Görünen hasar usta dökümü: Parça {tl(oz.toplam_parca)} + İşçilik {tl(oz.toplam_iscilik)} + Boya{" "}
                            {tl(oz.toplam_boya)} = {tl(oz.hasar_bedeli || oz.toplam_onarim_maliyeti)}
                        </p>
                        {oz.gizli_parca ? (
                            <p className="text-xs text-amber-200/70 mt-1">
                                {oz.gizli_parca} muhtemel gizli parça bilgi amaçlı — teklife dahil değil
                            </p>
                        ) : null}
                        <p className="text-xs text-slate-500 mt-1">
                            Rayiç referans: {tl(oz.rayic_deger)} · Onarım/rayiç: %{oz.onarim_rayic_oran ?? "—"}
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 mt-5">
                            <Button
                                variant="outline"
                                size="sm"
                                className="border-white/10"
                                onClick={teklifOnizle}
                                disabled={loading}
                            >
                                <Eye className="h-3.5 w-3.5 mr-1.5" />
                                HTML önizle
                            </Button>
                            <Button
                                size="sm"
                                className="bg-[#D4AF37] text-slate-900 font-bold hover:bg-[#c4a030]"
                                onClick={teklifPdfIndir}
                                disabled={loading}
                            >
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                PDF indir
                            </Button>
                        </div>
                    </div>

                    {/* Parça tablosu */}
                    {(teklif.parcalar || []).length > 0 ? (
                        <div className="rounded-xl border border-white/10 overflow-hidden">
                            <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02] flex items-center justify-between gap-2">
                                <h3 className="text-sm font-medium text-white">Parça maliyet dökümü</h3>
                                <span className="text-[10px] text-slate-500">Toplam yalnızca görünen hasar</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-slate-500 border-b border-white/5">
                                            <th className="text-left p-3 font-medium">Parça</th>
                                            <th className="text-left p-3 font-medium">Tip</th>
                                            <th className="text-left p-3 font-medium">İşlem</th>
                                            <th className="text-right p-3 font-medium">Parça</th>
                                            <th className="text-right p-3 font-medium">İşçilik</th>
                                            <th className="text-right p-3 font-medium">Boya</th>
                                            <th className="text-right p-3 font-medium">Toplam</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teklif.parcalar.map((p, i) => (
                                            <tr
                                                key={i}
                                                className={`border-b border-white/5 hover:bg-white/[0.02] ${
                                                    p.gorunurluk === "gizli_muhtemel" ? "opacity-60" : ""
                                                }`}
                                            >
                                                <td className="p-3 text-white">{p.parca_adi}</td>
                                                <td className="p-3">
                                                    {p.gorunurluk === "gizli_muhtemel" ? (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30">
                                                            Gizli muhtemel
                                                        </span>
                                                    ) : (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                                            Görünen
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-slate-400">{p.islem_tipi}</td>
                                                <td className="p-3 text-right text-slate-300">
                                                    {tl(p.parca_fiyat)}
                                                </td>
                                                <td className="p-3 text-right text-slate-300">
                                                    {tl(p.iscilik_tutar)}
                                                </td>
                                                <td className="p-3 text-right text-slate-300">
                                                    {tl(p.boya_tutar)}
                                                </td>
                                                <td className="p-3 text-right font-medium text-emerald-300">
                                                    {tl(p.toplam_maliyet)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-white/[0.03] font-medium">
                                            <td colSpan={3} className="p-3 text-slate-400">
                                                Toplam (görünen hasar)
                                            </td>
                                            <td className="p-3 text-right text-white">{tl(oz.toplam_parca)}</td>
                                            <td className="p-3 text-right text-white">
                                                {tl(oz.toplam_iscilik)}
                                            </td>
                                            <td className="p-3 text-right text-white">{tl(oz.toplam_boya)}</td>
                                            <td className="p-3 text-right text-[#D4AF37]">
                                                {tl(oz.toplam_onarim_maliyeti)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    {/* Bulgular */}
                    {(teklif.bulgular || []).length > 0 ? (
                        <div className="rounded-xl border border-white/10 p-4">
                            <p className="text-xs font-medium text-white mb-2">Bulgular & notlar</p>
                            <ul className="text-xs text-slate-400 space-y-1">
                                {teklif.bulgular.map((b, i) => (
                                    <li key={i}>• {b}</li>
                                ))}
                            </ul>
                            {teklif.metodoloji ? (
                                <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                                    {teklif.metodoloji}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* PDF önizleme modal */}
            {onizleAcik && onizleHtml ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                    <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                            <span className="text-sm font-medium text-slate-800">Teklif önizleme</span>
                            <button
                                type="button"
                                className="text-slate-500 hover:text-slate-800 text-sm"
                                onClick={() => setOnizleAcik(false)}
                            >
                                Kapat
                            </button>
                        </div>
                        <iframe
                            title="Teklif önizleme"
                            srcDoc={onizleHtml}
                            className="flex-1 w-full min-h-[60vh] border-0"
                        />
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div className="fixed bottom-6 right-6 flex items-center gap-2 rounded-xl bg-[#121A2F] border border-[#D4AF37]/30 px-4 py-3 shadow-xl z-40">
                    <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />
                    <span className="text-sm text-white">Analiz devam ediyor…</span>
                </div>
            ) : null}
        </div>
    );
}
